﻿import { useEffect, useMemo, useState } from "react"
import {
  fetchRelayCollections,
  discoveryFilter,
  recommendedRelaysForLanguage,
  relayUrlsFromDiscoveryEvents,
  type RelayCollection,
} from "@/nostr/api/relayDiscovery"
import {
  parseRelayReview,
  relayReviewFilter,
  reviewStats,
  type RelayReview,
} from "@/nostr/api/relayReviews"
import { relayInfoService } from "@/nostr/relayInfo"
import { KINDS, type RelayInfo } from "@/nostr/types"
import { useConfig } from "@/config/context"
import { useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { useSettings } from "@/state/SettingsContext"
import { uniqueRelayUrls } from "@/utils/url"

/** NIP-11 document for one relay, cached by the relay info service. */
export function useRelayInfo(url?: string): { info?: RelayInfo; loading: boolean } {
  const [info, setInfo] = useState<RelayInfo | undefined>(() =>
    url ? relayInfoService.getCached(url) : undefined,
  )
  const [loading, setLoading] = useState(Boolean(url))

  useEffect(() => {
    if (!url) {
      setInfo(undefined)
      setLoading(false)
      return
    }
    let cancelled = false
    const cached = relayInfoService.getCached(url)
    if (cached) setInfo(cached)
    setLoading(!cached)
    relayInfoService
      .get(url)
      .then((next) => {
        if (!cancelled) setInfo(next)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [url])

  return { info, loading }
}

/** Batch NIP-11 lookup for relay lists (explore grid, relay management). */
export function useRelayInfos(urls: string[]): {
  infos: Map<string, RelayInfo>
  loading: boolean
} {
  const [infos, setInfos] = useState<Map<string, RelayInfo>>(new Map())
  const [loading, setLoading] = useState(urls.length > 0)
  const key = urls.join("|")

  useEffect(() => {
    const unique = uniqueRelayUrls(urls)
    if (unique.length === 0) {
      setInfos(new Map())
      setLoading(false)
      return
    }
    let cancelled = false
    const initial = new Map<string, RelayInfo>()
    for (const url of unique) {
      const cached = relayInfoService.getCached(url)
      if (cached) initial.set(url, cached)
    }
    setInfos(initial)
    setLoading(initial.size < unique.length)

    Promise.all(unique.map((url) => relayInfoService.get(url)))
      .then((results) => {
        if (cancelled) return
        const next = new Map<string, RelayInfo>()
        results.forEach((info, index) => next.set(unique[index], info))
        setInfos(next)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { infos, loading }
}

/** Reviews (kind 31987) of a single relay. */
export function useRelayReviews(relayUrl?: string): {
  reviews: RelayReview[]
  stats: { average: number; count: number }
  loading: boolean
} {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const [reviews, setReviews] = useState<RelayReview[]>([])
  const [loading, setLoading] = useState(Boolean(relayUrl))

  useEffect(() => {
    if (!relayUrl) {
      setReviews([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const collected = new Map<string, RelayReview>()

    const subscription = pool.subscribe(relayReviewFilter(relayUrl, 100), {
      relays: appRelays,
      onEvent: (event) => {
        store.add(event)
        const review = parseRelayReview(event)
        if (!review || review.relayUrl !== relayUrl) return
        collected.set(review.pubkey, review)
        if (!cancelled) setReviews([...collected.values()].sort((a, b) => b.createdAt - a.createdAt))
      },
      onEose: () => {
        if (!cancelled) setLoading(false)
      },
    })
    return () => {
      cancelled = true
      subscription.close()
    }
  }, [relayUrl, pool, store, appRelays])

  return { reviews, stats: reviewStats(reviews), loading }
}

/** All relay reviews known to the discovery relays, newest first. */
export function useRelayReviewFeed(limit = 60): { reviews: RelayReview[]; loading: boolean } {
  const { pool, store } = useNostr()
  const { settings } = useSettings()
  const config = useConfig()
  const [reviews, setReviews] = useState<RelayReview[]>([])
  const [loading, setLoading] = useState(true)
  const relays = config.relays.discoveryRelays
  const relaysKey = relays.join("|")

  useEffect(() => {
    if (relays.length === 0) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const collected = new Map<string, RelayReview>()

    const subscription = pool.subscribe({ kinds: [KINDS.RELAY_REVIEW], limit }, {
      relays,
      onEvent: (event) => {
        store.add(event)
        const review = parseRelayReview(event)
        if (!review) return
        if (settings.hideOnionRelays && review.relayUrl.endsWith(".onion")) return
        collected.set(`${review.relayUrl}:${review.pubkey}`, review)
        if (!cancelled) setReviews([...collected.values()].sort((a, b) => b.createdAt - a.createdAt))
      },
      onEose: () => {
        if (!cancelled) setLoading(false)
      },
    })
    return () => {
      cancelled = true
      subscription.close()
    }
  }, [pool, store, relaysKey, limit, settings.hideOnionRelays])

  return { reviews, loading }
}

/**
 * Relay discovery sources: curated collections (awesome-nostr-relays),
 * language recommendations and relays announced by NIP-66 monitors.
 */
export function useRelayDiscovery(): {
  collections: RelayCollection[]
  loading: boolean
  error?: string
} {
  const config = useConfig()
  const { pool, store } = useNostr()
  const { settings } = useSettings()
  const language = settings.language
  const [curated, setCurated] = useState<RelayCollection[] | null>(null)
  const [discovered, setDiscovered] = useState<string[]>([])
  const [error, setError] = useState<string>()
  const [loadingCurated, setLoadingCurated] = useState(true)
  const [loadingDiscovery, setLoadingDiscovery] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoadingCurated(true)
    fetchRelayCollections(config.relays.discoveryCollectionsUrl, {
      filterOnion: settings.hideOnionRelays,
    })
      .then((collections) => {
        if (cancelled) return
        setCurated(collections)
        setError(undefined)
      })
      .catch((fetchError: unknown) => {
        if (cancelled) return
        setCurated([])
        setError(fetchError instanceof Error ? fetchError.message : String(fetchError))
      })
      .finally(() => {
        if (!cancelled) setLoadingCurated(false)
      })
    return () => {
      cancelled = true
    }
  }, [config.relays.discoveryCollectionsUrl, settings.hideOnionRelays])

  useEffect(() => {
    if (config.relays.discoveryRelays.length === 0) {
      setLoadingDiscovery(false)
      return
    }
    let cancelled = false
    setLoadingDiscovery(true)
    const found = new Set<string>()
    const subscription = pool.subscribe(discoveryFilter(250), {
      relays: config.relays.discoveryRelays,
      onEvent: (event) => {
        store.add(event)
        for (const url of relayUrlsFromDiscoveryEvents([event], settings.hideOnionRelays)) {
          found.add(url)
        }
        if (!cancelled) setDiscovered([...found])
      },
      onEose: () => {
        if (!cancelled) setLoadingDiscovery(false)
      },
    })
    return () => {
      cancelled = true
      subscription.close()
    }
  }, [pool, store, config.relays.discoveryRelays.join("|"), settings.hideOnionRelays])

  const collections = useMemo(() => {
    const recommended = recommendedRelaysForLanguage(language)
    const result: RelayCollection[] = []
    if (recommended.length > 0) {
      result.push({
        id: "recommended",
        name: "Recommended relays",
        relays: uniqueRelayUrls(recommended),
        source: "recommended",
      })
    }
    if (curated && curated.length > 0) result.push(...curated)
    if (discovered.length > 0) {
      result.push({
        id: "discovered",
        name: "Announced by relay monitors (NIP-66)",
        relays: uniqueRelayUrls(discovered).slice(0, 120),
        source: "discovery",
      })
    }
    return result
  }, [language, curated, discovered])

  return { collections, loading: loadingCurated && loadingDiscovery, error }
}
